import { FolderItem } from '../types';
import { getEntityType } from '../utils/entityUtils';

interface UseBatchOperationsProps {
  selectedEntities: Set<string>;
  setSelectedEntities: React.Dispatch<React.SetStateAction<Set<string>>>;
  setLastSelectedEntity: React.Dispatch<React.SetStateAction<any>>;
  onFolderDelete?: (folderId: string) => void;
  onItemDelete?: (itemId: string) => void;
  onFolderMove?: (folderId: string, newParentId: string | null) => void;
  onItemDrop?: (itemId: string, folderId: string | null) => void;
}

interface UseBatchOperationsReturn {
  handleBatchDelete: (allFolders: FolderItem[]) => Promise<void>;
  handleBatchMove: (targetFolderId: string | null, allFolders: FolderItem[]) => Promise<void>;
}

/**
 * Hook to manage batch operations on selected folders and items
 */
export const useBatchOperations = ({
  selectedEntities,
  setSelectedEntities,
  setLastSelectedEntity,
  onFolderDelete,
  onItemDelete,
  onFolderMove,
  onItemDrop,
}: UseBatchOperationsProps): UseBatchOperationsReturn => {
  
  const handleBatchDelete = async (allFolders: FolderItem[]) => {
    if (selectedEntities.size === 0) return;
    
    const folders: string[] = [];
    const items: string[] = [];
    
    // Categorize selected entities
    selectedEntities.forEach(id => {
      const entityType = getEntityType(id, allFolders);
      
      if (entityType === 'folder') {
        folders.push(id);
      } else if (entityType === 'item') {
        items.push(id);
      } else {
        // If we can't determine the type from the folder tree, assume it's an item
        items.push(id);
      }
    });
    
    // Delete folders (this will cascade to contained items if backend supports it)
    if (onFolderDelete && folders.length > 0) {
      for (const folderId of folders) {
        await onFolderDelete(folderId);
      }
    }
    
    // Delete items
    if (onItemDelete && items.length > 0) {
      for (const itemId of items) {
        await onItemDelete(itemId);
      }
    }
    
    // Clear selection
    setSelectedEntities(new Set());
    setLastSelectedEntity(null);
  };

  const handleBatchMove = async (targetFolderId: string | null, allFolders: FolderItem[]) => {
    if (selectedEntities.size === 0) return;
    
    const folders: string[] = [];
    const items: string[] = [];
    
    // Categorize selected entities
    selectedEntities.forEach(id => {
      const entityType = getEntityType(id, allFolders);
      
      if (entityType === 'folder') {
        folders.push(id);
      } else if (entityType === 'item') {
        items.push(id);
      } else {
        // If we can't determine the type from the folder tree, assume it's an item
        console.warn(`Could not determine type for entity ${id}, assuming item`);
        items.push(id);
      }
    });
    
    // Move folders
    if (onFolderMove && folders.length > 0) {
      for (const folderId of folders) {
        await onFolderMove(folderId, targetFolderId);
      }
    }
    
    // Move items
    if (onItemDrop && items.length > 0) {
      for (const itemId of items) {
        await onItemDrop(itemId, targetFolderId);
      }
    }
    
    // Clear selection after move
    setSelectedEntities(new Set());
    setLastSelectedEntity(null);
  };

  return {
    handleBatchDelete,
    handleBatchMove,
  };
};
